<template>
  <div class="welcome-card timeline-messages-card">
    <div class="card-header">
      <div class="card-title-group">
        <i class="fas fa-comment-alt card-title-icon"></i>
        <h3 class="card-title">Timeline Messages</h3>
      </div>
      <button class="view-all-link" @click="$emit('view-all')">
        View all <i class="fas fa-arrow-right"></i>
      </button>
    </div>

    <div class="card-body">
      <div v-if="isLoading" class="card-empty">Loading...</div>
      <div v-else-if="!entries.length" class="card-empty">
        No recent activity
      </div>
      <div v-for="entry in entries" :key="entry.id" class="message-row">
        <div class="message-avatar">
          <img
            v-if="entry.publisher.profile_picture"
            :src="entry.publisher.profile_picture"
            class="avatar-img"
            :alt="publisherName(entry.publisher)"
            @error="entry._avatarError = true"
          />
          <span v-else>{{ publisherInitials(entry.publisher) }}</span>
        </div>
        <div class="message-body">
          <div class="message-top-row">
            <span class="message-sender">{{
              publisherName(entry.publisher)
            }}</span>
            <span class="message-time">{{ formatTime(entry.created_at) }}</span>
          </div>
          <div class="message-preview">{{ buildDescription(entry) }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "TimelineMessagesCard",
  computed: {
    ...mapState({
      user: (state) => state.auth.user,
      entries: (state) => state.timeline.timelineEntries,
      isLoading: (state) => state.timeline.timelineLoading,
    }),
  },
  mounted() {
    if (this.user) {
      this.$store.dispatch("timeline/getTimeline", {
        userId: this.user.id,
        size: 3,
      });
    }
  },
  methods: {
    publisherName(publisher) {
      if (!publisher) return "Unknown";
      return `${publisher.first || ""} ${publisher.last || ""}`.trim();
    },
    publisherInitials(publisher) {
      if (!publisher) return "?";
      const f = (publisher.first || "").trim();
      const l = (publisher.last || "").trim();
      if (f && l) return (f[0] + l[0]).toUpperCase();
      if (f) return f.slice(0, 2).toUpperCase();
      return "?";
    },
    formatTime(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      const month = date.toLocaleDateString("en-US", { month: "long" });
      const day = String(date.getDate()).padStart(2, "0");
      const time = date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
      return `${month} ${day} · ${time}`;
    },
    buildDescription(item) {
      if (!item.publisher) return "";
      const pub = `${item.publisher.last}, ${item.publisher.first}`;
      const sub = item.subscriber
        ? `${item.subscriber.last}, ${item.subscriber.first}`
        : "";
      const rec = item.related_record_name || "";
      const map = {
        ASSIGNED_TASK: `${pub} assigned task (${rec}) to ${sub}`,
        ASSIGNED_TASK_UPDATE: `${pub} assigned task (${rec}) [Modified] to ${sub}`,
        ASSIGNED_TASK_REPLY: `${pub} replied to task (${rec}) assigned to ${sub}`,
        ASSIGNED_TASK_REPLY_UPDATE: `${pub} modified a reply on task (${rec}) assigned to ${sub}`,
        TAGGED_IN_TASK: `${sub} has been tagged in task (${rec}) by ${pub}`,
        TAGGED_IN_TASK_UPDATE: `${sub} has been tagged in task (${rec}) [Modified] by ${pub}`,
        TAGGED_IN_TASK_REPLY: `${pub} replied to task (${rec}) where ${sub} tagged in`,
        TAGGED_IN_REPLY: `${sub} has been tagged in a reply for task (${rec}) by ${pub}`,
        TAGGED_IN_REPLY_UPDATE: `${sub} has been tagged in a reply for task (${rec}) [Modified] by ${pub}`,
        AUTHOR_TASK_REPLY: `${pub} replied on a task (${rec}) created by ${sub}`,
        AUTHOR_TASK_REPLY_UPDATE: `${pub} modified a reply on a task (${rec}) created by ${sub}`,
        REPLY_COMMENT: `${pub} commented on reply for task (${rec}) added by ${sub}`,
        REPLY_COMMENT_UPDATE: `${pub} updated a comment on reply for task (${rec}) added by ${sub}`,
        TASK_REACTION: `${pub} reacted to task (${rec})`,
        REPLY_REACTION: `${pub} reacted to a reply on task (${rec})`,
        SHARED_IN_FOLDER: `${pub} shared the folder (${rec}) with ${sub}`,
        SHARED_IN_DOCUMENT: `${pub} shared the file (${rec}) with ${sub}`,
      };
      return map[item.type] || `${pub} — ${rec}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.welcome-card {
  background: var(--oliver-bg-surface, rgba(255, 255, 255, 0.7));
  border: 1px solid var(--oliver-border, rgba(0, 0, 0, 0.06));
  border-radius: 14px;
  padding: 10px 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 100%;
  box-sizing: border-box;
  overflow: hidden;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  backdrop-filter: blur(6px);

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0;
  background: none;
  border: none;
}

.card-title-group {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.card-title-icon {
  font-size: 14px;
  color: var(--oliver-accent);
  flex-shrink: 0;
}

.card-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--oliver-text);
  margin: 0;
  white-space: nowrap;
}

.view-all-link {
  background: none;
  border: none;
  color: var(--oliver-accent);
  opacity: 0.85;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 6px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  transition: opacity 0.2s ease;

  i {
    font-size: 10px;
  }

  &:hover {
    opacity: 1;
  }
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0;
  flex: 1;
  overflow-y: auto;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.15);
    border-radius: 2px;
  }
}

.card-empty {
  font-size: 13px;
  color: var(--oliver-greeting-subtitle);
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 0;
}

.message-row {
  display: flex;
  gap: 9px;
  align-items: flex-start;
  min-width: 0;
}

.message-avatar {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--oliver-accent);
  color: var(--oliver-text);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: 0.5px;
  overflow: hidden;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.message-body {
  flex: 1;
  min-width: 0;
}

.message-top-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 6px;
  margin-bottom: 2px;
  flex-wrap: wrap;
}

.message-sender {
  font-size: 13px;
  font-weight: 600;
  color: var(--oliver-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.message-time {
  font-size: 11px;
  color: var(--oliver-text);
  opacity: 0.55;
  white-space: nowrap;
  flex-shrink: 0;
}

.message-preview {
  font-size: 12px;
  color: var(--oliver-text);
  opacity: 0.75;
  line-height: 1.3;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
}
</style>
